import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Plus, Trash2, Infinity as InfinityIcon } from "lucide-react";

const empty = {
  name: "", phone: "", email: "", address: "", date_of_birth: "", age: "",
  blood_group: "", father_name: "", mother_name: "", parent_phone: "", parent_occupation: "",
  religion: "", caste: "", category: "", nationality: "Indian", gender: "",
  height: "", chest: "", weight: "",
  desired_post: "Police Bharti", training_duration: "12 months",
  facilities: [],
  program_ids: [], status: "inquiry",
  photo_url: "",
  education: [
    { class_name: "10th", year: "", percentage: "" },
    { class_name: "12th", year: "", percentage: "" },
  ],
};

const FACILITY_OPTIONS = [
  { key: "dress_code", label_en: "Dress Code", label_mr: "ड्रेस कोड" },
  { key: "ground", label_en: "Ground", label_mr: "ग्राऊंड" },
  { key: "lecture", label_en: "Lecture", label_mr: "लेक्चर" },
  { key: "study_hall", label_en: "Study Hall", label_mr: "अभ्यासिका" },
  { key: "hostel", label_en: "Hostel", label_mr: "राहण्याची सोय" },
  { key: "mess", label_en: "Mess (Food)", label_mr: "जेवणाची सोय" },
];

export default function StudentForm({ initial, onSubmit, submitLabel = "Submit", isPublic = false, saving = false }) {
  const [form, setForm] = useState({ ...empty, ...(initial || {}), program_ids: initial?.program_ids || (initial?.program_id ? [initial.program_id] : []) });
  const [programs, setPrograms] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(initial?.photo_url || "");

  useEffect(() => {
    const url = isPublic ? "/programs/public" : "/programs";
    api.get(url).then((r) => {
      setPrograms(r.data);
      if (r.data[0] && !form.program_id) setForm((f) => ({ ...f, program_id: r.data[0].id }));
    });
    // eslint-disable-next-line
  }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "object" && e?.target ? e.target.value : e }));

  const updEdu = (i, k, v) => {
    const ed = [...form.education];
    ed[i] = { ...ed[i], [k]: v };
    setForm({ ...form, education: ed });
  };
  const addEdu = () => setForm({ ...form, education: [...form.education, { class_name: "", year: "", percentage: "" }] });
  const delEdu = (i) => setForm({ ...form, education: form.education.filter((_, idx) => idx !== i) });

  const toggleProgram = (pid) => {
    setForm((f) => {
      const ids = new Set(f.program_ids || []);
      if (ids.has(pid)) ids.delete(pid); else ids.add(pid);
      return { ...f, program_ids: Array.from(ids) };
    });
  };

  const toggleFacility = (key) => {
    setForm((f) => {
      const cur = new Set(f.facilities || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...f, facilities: Array.from(cur) };
    });
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      alert("Photo must be under 1 MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
      setForm((f) => ({ ...f, photo_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.program_ids || form.program_ids.length === 0) {
      alert("Please select at least one program.");
      return;
    }
    const payload = { ...form };
    if (payload.age === "" || payload.age === undefined) payload.age = null;
    else if (payload.age) payload.age = parseInt(payload.age) || null;
    payload.education = (payload.education || []).filter((x) => x.class_name);
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Photo + Personal */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4" style={{ fontFamily: "Chivo" }}>Personal Information / वैयक्तिक माहिती</h3>
        <div className="flex flex-col md:flex-row gap-6">
          {/* Photo */}
          <div className="flex flex-col items-center">
            <div className="w-32 h-40 border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center overflow-hidden bg-slate-50">
              {photoPreview ? (
                <img src={photoPreview} alt="student" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-slate-400">
                  <Camera size={28} className="mx-auto mb-1" />
                  <span className="text-xs">PHOTO</span>
                </div>
              )}
            </div>
            <label className="mt-2 cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="student-photo-input" />
              <span className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded-md text-xs font-medium hover:bg-slate-100">
                Upload Photo
              </span>
            </label>
          </div>

          {/* Personal fields */}
          <div className="flex-1 grid md:grid-cols-2 gap-4">
            <div>
              <Label className="label-caps">Full Name / नाव *</Label>
              <Input data-testid="form-name" required value={form.name} onChange={upd("name")} className="mt-2 h-11" />
            </div>
            <div>
              <Label className="label-caps">Mobile / मो.नं. *</Label>
              <Input data-testid="form-phone" required maxLength={10} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })} className="mt-2 h-11 font-mono" />
            </div>
            <div>
              <Label className="label-caps">Date of Birth / जन्मतारीख</Label>
              <Input data-testid="form-dob" type="date" value={form.date_of_birth} onChange={upd("date_of_birth")} className="mt-2 h-11" />
            </div>
            <div>
              <Label className="label-caps">Age / वय</Label>
              <Input data-testid="form-age" type="number" min="14" max="60" value={form.age} onChange={upd("age")} className="mt-2 h-11" />
            </div>
            <div>
              <Label className="label-caps">Blood Group / रक्तगट</Label>
              <Select value={form.blood_group} onValueChange={upd("blood_group")}>
                <SelectTrigger data-testid="form-blood" className="mt-2 h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Email</Label>
              <Input data-testid="form-email" type="email" value={form.email} onChange={upd("email")} className="mt-2 h-11" />
            </div>
            <div className="md:col-span-2">
              <Label className="label-caps">Address / पत्ता</Label>
              <Textarea data-testid="form-address" value={form.address} onChange={upd("address")} rows={2} className="mt-2" />
            </div>
          </div>
        </div>
      </Card>

      {/* Parents */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4" style={{ fontFamily: "Chivo" }}>Parents / पालकांची माहिती</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="label-caps">Father's Name / वडिलांचे नाव</Label>
            <Input data-testid="form-father" value={form.father_name} onChange={upd("father_name")} className="mt-2 h-11" />
          </div>
          <div>
            <Label className="label-caps">Mother's Name / आईचे नाव</Label>
            <Input data-testid="form-mother" value={form.mother_name} onChange={upd("mother_name")} className="mt-2 h-11" />
          </div>
          <div>
            <Label className="label-caps">Parent Mobile / पालक मो.नं.</Label>
            <Input data-testid="form-parent-phone" maxLength={10} value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value.replace(/\D/g, "") })} className="mt-2 h-11 font-mono" />
          </div>
          <div>
            <Label className="label-caps">Occupation / व्यवसाय</Label>
            <Input data-testid="form-occupation" value={form.parent_occupation} onChange={upd("parent_occupation")} className="mt-2 h-11" />
          </div>
        </div>
      </Card>

      {/* Demographics */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4" style={{ fontFamily: "Chivo" }}>Background / पार्श्वभूमी</h3>
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <Label className="label-caps">Religion / धर्म</Label>
            <Input data-testid="form-religion" value={form.religion} onChange={upd("religion")} className="mt-2 h-11" />
          </div>
          <div>
            <Label className="label-caps">Caste / जात</Label>
            <Input data-testid="form-caste" value={form.caste} onChange={upd("caste")} className="mt-2 h-11" />
          </div>
          <div>
            <Label className="label-caps">Category / संवर्ग</Label>
            <Select value={form.category} onValueChange={upd("category")}>
              <SelectTrigger className="mt-2 h-11"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {["Open", "OBC", "SC", "ST", "VJ/DT", "NT", "SBC", "EWS"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Nationality / राष्ट्रीयत्व</Label>
            <Input value={form.nationality} onChange={upd("nationality")} className="mt-2 h-11" />
          </div>
          <div>
            <Label className="label-caps">Gender / लिंग</Label>
            <Select value={form.gender} onValueChange={upd("gender")}>
              <SelectTrigger data-testid="form-gender" className="mt-2 h-11"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male / पुरुष</SelectItem>
                <SelectItem value="Female">Female / स्त्री</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Height / उंची (cm)</Label>
            <Input data-testid="form-height" value={form.height} onChange={upd("height")} className="mt-2 h-11" placeholder="e.g. 170" />
          </div>
          <div>
            <Label className="label-caps">Chest / छाती (cm)</Label>
            <Input data-testid="form-chest" value={form.chest} onChange={upd("chest")} className="mt-2 h-11" placeholder="e.g. 80-85" />
          </div>
          <div>
            <Label className="label-caps">Weight / वजन (kg)</Label>
            <Input data-testid="form-weight" value={form.weight} onChange={upd("weight")} className="mt-2 h-11" placeholder="e.g. 65" />
          </div>
        </div>
      </Card>

      {/* Education */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold" style={{ fontFamily: "Chivo" }}>Educational Info / शैक्षणिक माहिती</h3>
          <Button type="button" size="sm" variant="outline" onClick={addEdu} data-testid="add-edu-row">
            <Plus size={14} className="mr-1" /> Add row
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left label-caps">Class / वर्ग</th>
                <th className="px-3 py-2 text-left label-caps">Year / वर्ष</th>
                <th className="px-3 py-2 text-left label-caps">% / टक्केवारी</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.education.map((row, i) => (
                <tr key={i}>
                  <td className="p-2">
                    <Input value={row.class_name} onChange={(e) => updEdu(i, "class_name", e.target.value)} placeholder="10th / 12th / Degree" data-testid={`edu-class-${i}`} />
                  </td>
                  <td className="p-2"><Input value={row.year} onChange={(e) => updEdu(i, "year", e.target.value)} placeholder="2023" /></td>
                  <td className="p-2"><Input value={row.percentage} onChange={(e) => updEdu(i, "percentage", e.target.value)} placeholder="75" /></td>
                  <td className="p-2">
                    {form.education.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => delEdu(i)}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Program & training */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4" style={{ fontFamily: "Chivo" }}>Program / प्रशिक्षण</h3>
        <div>
          <Label className="label-caps mb-2 block">Select Programs / प्रोग्राम्स निवडा *</Label>
          <p className="text-xs text-slate-500 mb-3">You can select one or more programs (e.g., both Police & Army preparation).</p>
          <div className="grid md:grid-cols-2 gap-3" data-testid="form-programs-list">
            {programs.map((p) => {
              const checked = form.program_ids.includes(p.id);
              return (
                <label
                  key={p.id}
                  className={`flex items-start gap-3 p-3 rounded-md border-2 cursor-pointer transition ${checked ? "border-lime-500 bg-lime-50" : "border-slate-200 hover:bg-slate-50"}`}
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggleProgram(p.id)} data-testid={`form-program-${p.code || p.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold flex items-center gap-2">
                      {p.name}
                      {p.is_lifetime && <InfinityIcon size={12} className="text-lime-700" />}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {p.is_lifetime ? "Lifetime" : `${p.duration_months} months`} · ₹{(p.total_fee || 0).toLocaleString("en-IN")}
                    </div>
                  </div>
                </label>
              );
            })}
            {programs.length === 0 && <div className="text-sm text-slate-500 col-span-2">No programs available. Ask admin to create programs first.</div>}
          </div>
        </div>
        <div className="mt-6">
          <Label className="label-caps">Desired Post / पद</Label>
          <Select value={form.desired_post} onValueChange={upd("desired_post")}>
            <SelectTrigger data-testid="form-desired" className="mt-2 h-11 md:w-1/2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Police Bharti">पोलीस भरती</SelectItem>
              <SelectItem value="Army Bharti">आर्मी भरती</SelectItem>
              <SelectItem value="Both">Both / दोन्ही</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Our Facilities - multi select (BETWEEN desired post and training duration) */}
        <div className="mt-6">
          <Label className="label-caps mb-2 block">Our Facilities / आमच्या सुविधा</Label>
          <p className="text-xs text-slate-500 mb-3">Select the facilities you require (multiple allowed).</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="form-facilities-list">
            {FACILITY_OPTIONS.map((f) => {
              const checked = (form.facilities || []).includes(f.key);
              return (
                <label
                  key={f.key}
                  className={`flex items-center gap-3 p-3 rounded-md border-2 cursor-pointer transition ${checked ? "border-lime-500 bg-lime-50" : "border-slate-200 hover:bg-slate-50"}`}
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggleFacility(f.key)} data-testid={`form-facility-${f.key}`} />
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{f.label_en}</div>
                    <div className="text-xs text-slate-500">{f.label_mr}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="mt-6">
          <Label className="label-caps">Training Duration / प्रशिक्षण कालावधी</Label>
          <Select value={form.training_duration} onValueChange={upd("training_duration")}>
            <SelectTrigger data-testid="form-duration" className="mt-2 h-11 md:w-1/2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3 months">3 months / ३ महिने</SelectItem>
              <SelectItem value="6 months">6 months / ६ महिने</SelectItem>
              <SelectItem value="12 months">12 months / १२ महिने</SelectItem>
              <SelectItem value="until_selection">Until Bharti / भरती पर्यंत</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {!isPublic && (
          <p className="text-xs text-slate-500 mt-3">
            ℹ️ Fee structure and installments will be configured from the student's detail page after creation.
          </p>
        )}
        {isPublic && (
          <p className="text-xs text-amber-700 mt-3 p-2 bg-amber-50 border border-amber-200 rounded">
            Your registration will be reviewed by an administrator. You will be able to login after approval.
          </p>
        )}
      </Card>

      <Button type="submit" disabled={saving} data-testid="form-submit" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-base">
        {saving ? "Saving…" : submitLabel}
      </Button>
    </form>
  );
}
